import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Link } from "react-router-dom";
import L from "leaflet";
import { imgSrc } from "../api";

// Singapore
const DEFAULT_CENTER = [1.3521, 103.8198];
// Bounding box around Singapore (SW + NE corners) — panning is locked to this.
const SG_BOUNDS = [
  [1.13, 103.55],
  [1.50, 104.15],
];

// ---------- NEA rain-area radar (weather.gov.sg) ----------
// Geographic extent of the 70km dpsri radar PNG (SW corner, NE corner).
const RAIN_BOUNDS = [
  [1.156, 103.565],
  [1.475, 104.13],
];
const RAIN_STEP_MS = 5 * 60 * 1000; // radar publishes a frame every 5 min
const RAIN_HOURS = 12; // how far back to scrub
const RAIN_LAG_FRAMES = 2; // newest 1–2 frames may not be published yet → skip them

const pad = (n) => String(n).padStart(2, "0");

// Singapore is a fixed UTC+8 (no DST). Reading the UTC fields of an
// epoch shifted by +8h gives the SGT wall-clock regardless of browser TZ.
function sgtFields(ms) {
  const d = new Date(ms + 8 * 3600 * 1000);
  return {
    Y: d.getUTCFullYear(),
    Mo: d.getUTCMonth() + 1,
    D: d.getUTCDate(),
    H: d.getUTCHours(),
    Mi: d.getUTCMinutes(),
  };
}

// Build a list of radar frames for the past RAIN_HOURS, oldest → newest.
function buildRainFrames() {
  // Floor "now" to a 5-min boundary, then back off a couple of frames so we
  // don't request the freshest timestamp before NEA has published it.
  const tip =
    Math.floor(Date.now() / RAIN_STEP_MS) * RAIN_STEP_MS -
    RAIN_LAG_FRAMES * RAIN_STEP_MS;
  const count = (RAIN_HOURS * 60 * 60 * 1000) / RAIN_STEP_MS;
  const frames = [];
  for (let i = count - 1; i >= 0; i--) {
    const ms = tip - i * RAIN_STEP_MS;
    const f = sgtFields(ms);
    const stamp = `${f.Y}${pad(f.Mo)}${pad(f.D)}${pad(f.H)}${pad(f.Mi)}`;
    frames.push({
      url: `https://www.weather.gov.sg/files/rainarea/50km/v2/dpsri_70km_${stamp}0000dBR.dpsri.png`,
      label: `${pad(f.H)}:${pad(f.Mi)}`,
      day: `${pad(f.D)}/${pad(f.Mo)}`,
      ms,
    });
  }
  return frames;
}

// Single Leaflet ImageOverlay whose source swaps as the slider moves.
function RainLayer({ frame }) {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    const layer = L.imageOverlay(frame ? frame.url : "", RAIN_BOUNDS, {
      opacity: 0.75,
      interactive: false,
      className: "rain-overlay",
      zIndex: 400,
    });
    layer.addTo(map);
    layerRef.current = layer;
    return () => {
      map.removeLayer(layer);
      layerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (layerRef.current && frame) layerRef.current.setUrl(frame.url);
  }, [frame]);

  return null;
}

// A spot is treated as a skatepark when its name says so (e.g. "Bishan
// Skate Park", "Woodlands Skatepark") — those markers are drawn in red.
const isSkatepark = (s) => /skate ?park/i.test(s?.name || "");

const makePin = (active, park) =>
  L.divIcon({
    className:
      "spot-pin" + (active ? " active" : "") + (park ? " skatepark" : ""),
    html: '<div class="pin"></div>',
    iconSize: [18, 18],
    iconAnchor: [9, 18],
    popupAnchor: [0, -16],
  });

const userIcon = L.divIcon({
  className: "user-pin",
  html: '<div class="dot"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// Flies the map to the selected spot and opens its popup.
function MapController({ selected, markerRefs }) {
  const map = useMap();
  useEffect(() => {
    if (!selected || selected.lat == null || selected.lng == null) return;
    map.flyTo([Number(selected.lat), Number(selected.lng)], 16, {
      duration: 1,
    });
    const m = markerRefs.current[selected.spotID];
    if (m) setTimeout(() => m.openPopup(), 350);
  }, [selected, map, markerRefs]);
  return null;
}

// Flies the map to the user's location once it's found.
function LocateFlyer({ pos }) {
  const map = useMap();
  useEffect(() => {
    if (pos) map.flyTo(pos, 15, { duration: 1 });
  }, [pos, map]);
  return null;
}

export default function SpotMap({ spots, selected }) {
  const markerRefs = useRef({});
  const [userPos, setUserPos] = useState(null);
  const [locating, setLocating] = useState(false);

  // ---- Rain radar state ----
  const [rainOn, setRainOn] = useState(false);
  const [frames, setFrames] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);

  const toggleRain = () => {
    if (rainOn) {
      setRainOn(false);
      setPlaying(false);
      return;
    }
    const f = buildRainFrames();
    setFrames(f);
    setIdx(f.length - 1); // start on the most recent frame
    setRainOn(true);
    // Warm the browser cache so scrubbing/playback is flicker-free.
    f.forEach((fr) => {
      const im = new Image();
      im.src = fr.url;
    });
  };

  // Auto-advance while playing; loop back to the start at the end.
  useEffect(() => {
    if (!playing || !rainOn || frames.length === 0) return;
    const id = setInterval(() => {
      setIdx((p) => (p >= frames.length - 1 ? 0 : p + 1));
    }, 320);
    return () => clearInterval(id);
  }, [playing, rainOn, frames.length]);

  const current = frames[idx];

  const locateMe = () => {
    if (!navigator.geolocation) {
      alert("Location isn't supported by this browser.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserPos([pos.coords.latitude, pos.coords.longitude]);
        setLocating(false);
      },
      (err) => {
        alert("Couldn't get your location: " + err.message);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  return (
    <>
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={12}
        minZoom={11}
        maxZoom={18}
        maxBounds={SG_BOUNDS}
        maxBoundsViscosity={1.0}
        scrollWheelZoom
        zoomControl
      >
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        <MapController selected={selected} markerRefs={markerRefs} />
        <LocateFlyer pos={userPos} />
        {rainOn && current && <RainLayer frame={current} />}
        {userPos && (
          <Marker position={userPos} icon={userIcon}>
            <Popup>You are here</Popup>
          </Marker>
        )}
        {spots
          .filter((s) => s.lat != null && s.lng != null)
          .map((s) => {
            const thumb = s.images && s.images[0] && s.images[0].imgURL;
            return (
              <Marker
                key={s.spotID}
                position={[Number(s.lat), Number(s.lng)]}
                icon={makePin(
                  selected && selected.spotID === s.spotID,
                  isSkatepark(s)
                )}
                ref={(ref) => {
                  if (ref) markerRefs.current[s.spotID] = ref;
                }}
              >
                <Popup>
                  <div className="popup-card">
                    {thumb && <img src={imgSrc(thumb)} alt={s.name} />}
                    <div className="body">
                      <h4>{s.name}</h4>
                      <Link className="btn" to={`/spots/${s.spotID}`}>
                        View details →
                      </Link>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
      </MapContainer>

      {rainOn && frames.length > 0 && (
        <div className="rain-panel">
          <div className="rain-readout">
            <span className="rain-time">{current ? current.label : "--:--"}</span>
            <span className="rain-day">{current ? current.day : "SGT"}</span>
          </div>
          <button
            className="rain-play"
            onClick={() => setPlaying((p) => !p)}
            title={playing ? "Pause" : "Play"}
            aria-label={playing ? "Pause animation" : "Play animation"}
          >
            {playing ? "⏸" : "▶"}
          </button>
          <input
            className="rain-slider"
            type="range"
            min={0}
            max={frames.length - 1}
            value={idx}
            onChange={(e) => {
              setPlaying(false);
              setIdx(Number(e.target.value));
            }}
            aria-label="Scrub rain radar over the past 12 hours"
            title="Past 12 h · drag to scrub"
          />
          <span
            className="rain-legend-bar"
            title="Rain intensity: light → heavy"
          />
        </div>
      )}

      <button
        className={"rain-btn" + (rainOn ? " active" : "")}
        onClick={toggleRain}
        title={rainOn ? "Hide rain radar" : "Show rain radar"}
        aria-label="Toggle rain radar"
        aria-pressed={rainOn}
      >
        🌧️
      </button>

      <button
        className="locate-btn"
        onClick={locateMe}
        disabled={locating}
        title="Find my location"
        aria-label="Find my location"
      >
        {locating ? "…" : "📍"}
      </button>
    </>
  );
}
